import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ChangeEvent } from 'react'
import { Image as ImageIcon, Loader2, RefreshCw, Save, Upload } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { useToast } from '../components/ui/toast'
import {
  createDefaultProfilePicture,
  getDefaultProfilePictures,
  replaceDefaultProfilePictureImage,
  updateDefaultProfilePicture,
  type DefaultProfilePicture
} from '../lib/api/defaultProfilePictures'

type PictureDraft = {
  name: string
  sortOrder: string
  isActive: boolean
}

const toDraft = (picture: DefaultProfilePicture): PictureDraft => ({
  name: picture.name,
  sortOrder: String(picture.sortOrder),
  isActive: picture.isActive
})

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(value))
  } catch {
    return value
  }
}

export const DefaultProfilePicturesPage = () => {
  const { addToast } = useToast()
  const [pictures, setPictures] = useState<DefaultProfilePicture[]>([])
  const [drafts, setDrafts] = useState<Record<string, PictureDraft>>({})
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [replacingId, setReplacingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newSortOrder, setNewSortOrder] = useState('')
  const [newIsActive, setNewIsActive] = useState(true)
  const [newFile, setNewFile] = useState<File | null>(null)

  const hydrate = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getDefaultProfilePictures()
      setPictures(data)
      setDrafts(Object.fromEntries(data.map((picture) => [picture.id, toDraft(picture)])))
    } catch (error) {
      console.error('Failed to load default profile pictures', error)
      addToast({
        title: 'Could not load default profile pictures',
        description: 'Please refresh or try again later.',
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    void hydrate()
  }, [hydrate])

  const activeCount = useMemo(() => pictures.filter((picture) => picture.isActive).length, [pictures])

  const updateDraft = (id: string, patch: Partial<PictureDraft>) => {
    setDrafts((current) => ({
      ...current,
      [id]: {
        ...current[id],
        ...patch
      }
    }))
  }

  const handleCreate = async () => {
    if (!newFile) {
      addToast({
        title: 'Choose an image first',
        variant: 'error'
      })
      return
    }

    setCreating(true)
    try {
      const created = await createDefaultProfilePicture(newFile, {
        name: newName.trim() || null,
        sortOrder: newSortOrder.trim() ? Number(newSortOrder) : null,
        isActive: newIsActive
      })
      setPictures((current) => [...current, created].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)))
      setDrafts((current) => ({ ...current, [created.id]: toDraft(created) }))
      setNewFile(null)
      setNewName('')
      setNewSortOrder('')
      setNewIsActive(true)
      addToast({
        title: 'Default profile picture added',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to create default profile picture', error)
      addToast({
        title: 'Could not add default profile picture',
        description: 'The image was not uploaded.',
        variant: 'error'
      })
    } finally {
      setCreating(false)
    }
  }

  const handleSave = async (picture: DefaultProfilePicture) => {
    const draft = drafts[picture.id]
    if (!draft) return

    setSavingId(picture.id)
    try {
      const updated = await updateDefaultProfilePicture(picture.id, {
        name: draft.name.trim(),
        sortOrder: draft.sortOrder.trim() ? Number(draft.sortOrder) : 0,
        isActive: draft.isActive
      })
      setPictures((current) => current
        .map((item) => item.id === updated.id ? updated : item)
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)))
      setDrafts((current) => ({ ...current, [updated.id]: toDraft(updated) }))
      addToast({
        title: 'Default profile picture saved',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to save default profile picture', error)
      addToast({
        title: 'Could not save default profile picture',
        variant: 'error'
      })
    } finally {
      setSavingId(null)
    }
  }

  const handleReplace = async (picture: DefaultProfilePicture, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    setReplacingId(picture.id)
    try {
      const updated = await replaceDefaultProfilePictureImage(picture.id, file)
      setPictures((current) => current.map((item) => item.id === updated.id ? updated : item))
      setDrafts((current) => ({ ...current, [updated.id]: toDraft(updated) }))
      addToast({
        title: 'Default profile picture replaced',
        variant: 'success'
      })
    } catch (error) {
      console.error('Failed to replace default profile picture', error)
      addToast({
        title: 'Could not replace default profile picture',
        description: 'The existing default was not changed.',
        variant: 'error'
      })
    } finally {
      setReplacingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Default Profile Pictures</h1>
          <p className="mt-1 text-sm text-muted-foreground">{activeCount} active defaults available for mobile users.</p>
        </div>
        <Button variant="outline" onClick={hydrate} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add default</CardTitle>
          <CardDescription>Upload a reusable profile picture for admin resets.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 lg:grid-cols-[1fr_160px_140px_auto] lg:items-end">
          <label className="block space-y-2">
            <span className="text-sm font-medium text-foreground">Image</span>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(event) => setNewFile(event.target.files?.[0] ?? null)}
              disabled={creating}
            />
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium text-foreground">Name</span>
            <Input value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="Trail green" disabled={creating} />
          </label>
          <label className="block space-y-2">
            <span className="text-sm font-medium text-foreground">Order</span>
            <Input type="number" value={newSortOrder} onChange={(event) => setNewSortOrder(event.target.value)} placeholder="Auto" disabled={creating} />
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={newIsActive}
                onChange={(event) => setNewIsActive(event.target.checked)}
                className="h-4 w-4 rounded border-border"
                disabled={creating}
              />
              Active
            </label>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading default profile pictures
          </CardContent>
        </Card>
      ) : pictures.length === 0 ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <ImageIcon className="h-4 w-4" />
            No default profile pictures have been uploaded.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {pictures.map((picture) => {
            const draft = drafts[picture.id] ?? toDraft(picture)
            const isDirty =
              draft.name !== picture.name ||
              Number(draft.sortOrder || 0) !== picture.sortOrder ||
              draft.isActive !== picture.isActive
            const saving = savingId === picture.id
            const replacing = replacingId === picture.id

            return (
              <Card key={picture.id}>
                <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-4">
                    <img
                      src={picture.imageUrl}
                      alt={`${picture.name} default avatar`}
                      className="h-20 w-20 rounded-full border border-border object-cover"
                    />
                    <div className="space-y-1">
                      <CardTitle className="text-base">{picture.name}</CardTitle>
                      <CardDescription>Updated {formatDate(picture.updatedAt)}</CardDescription>
                    </div>
                  </div>
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${picture.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-zinc-100 text-zinc-600'}`}>
                    {picture.isActive ? 'Active' : 'Inactive'}
                  </span>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-[1fr_120px_auto] sm:items-end">
                    <label className="block space-y-2">
                      <span className="text-sm font-medium text-foreground">Name</span>
                      <Input value={draft.name} onChange={(event) => updateDraft(picture.id, { name: event.target.value })} disabled={saving} />
                    </label>
                    <label className="block space-y-2">
                      <span className="text-sm font-medium text-foreground">Order</span>
                      <Input type="number" value={draft.sortOrder} onChange={(event) => updateDraft(picture.id, { sortOrder: event.target.value })} disabled={saving} />
                    </label>
                    <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={draft.isActive}
                        onChange={(event) => updateDraft(picture.id, { isActive: event.target.checked })}
                        className="h-4 w-4 rounded border-border"
                        disabled={saving}
                      />
                      Active
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => handleSave(picture)} disabled={!isDirty || saving}>
                      {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                      Save
                    </Button>
                    <label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={(event) => handleReplace(picture, event)}
                        disabled={replacing}
                      />
                      <span className="inline-flex h-8 cursor-pointer items-center justify-center rounded-md border-2 border-primary/30 bg-transparent px-3 text-xs font-semibold text-primary transition-all hover:border-primary hover:bg-primary/5">
                        {replacing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                        Replace image
                      </span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
